import EmblaCarousel from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import { select, addClass, removeClass } from 'helpers/dom'

const selectors = {
  VIEWPORT_SELECTOR: '.js-embla-viewport',
  DOTS_SELECTOR: '.js-embla-dots',
  DOT_ITEM_CLASS: 'embla-dot',
  DOT_ITEM_SELECTED_CLASS: 'embla-dot-selected',
  PREV_BUTTON_SELECTOR: '.js-embla-button-prev',
  NEXT_BUTTON_SELECTOR: '.js-embla-button-next'
}

class EmblaCarouselInstance {
  constructor(element, options, plugins) {
    this.carousel = element
    this.viewport = select(selectors.VIEWPORT_SELECTOR, this.carousel)
    this.dots = select(selectors.DOTS_SELECTOR, this.carousel)
    this.nextButton = select(selectors.NEXT_BUTTON_SELECTOR, this.carousel)
    this.prevButton = select(selectors.PREV_BUTTON_SELECTOR, this.carousel)

    this.options = options || {}
    this.plugins = plugins.autoplay ? [Autoplay({ delay: Number(plugins.autoplay), stopOnInteraction: false })] : []

    this.emblaApi = EmblaCarousel(this.viewport, this.options, this.plugins)

    this.removeButtonsHandle = this.buttonsClickHandle(this.emblaApi, this.prevButton, this.nextButton, this.onButtonClick)
    this.removeDotsHandle = this.dotsClickHandle(this.emblaApi, this.dots, this.onButtonClick)

    this.emblaApi.on('destroy', this.removeButtonsHandle)
    this.emblaApi.on('destroy', this.removeDotsHandle)
  }

  onButtonClick(emblaApi) {
    const { autoplay } = emblaApi.plugins()

    if (!autoplay) return
    if (autoplay.options.stopOnInteraction !== false) autoplay.stop()
  }

  toggleButtonsState(emblaApi, prevButton, nextButton) {
    const buttonStateHandle = () => {
      if (emblaApi.canScrollPrev()) prevButton.removeAttribute('disabled')
      else prevButton.setAttribute('disabled', 'disabled')

      if (emblaApi.canScrollNext()) nextButton.removeAttribute('disabled')
      else nextButton.setAttribute('disabled', 'disabled')
    }

    emblaApi.on('select', buttonStateHandle).on('init', buttonStateHandle).on('reInit', buttonStateHandle)

    return () => {
      prevButton.removeAttribute('disabled')
      nextButton.removeAttribute('disabled')
    }
  }

  buttonsClickHandle(emblaApi, prevButton, nextButton, onButtonClick) {
    const scrollPrev = () => {
      emblaApi.scrollPrev()
      if (onButtonClick) onButtonClick(emblaApi)
    }

    const scrollNext = () => {
      emblaApi.scrollNext()
      if (onButtonClick) onButtonClick(emblaApi)
    }

    prevButton.addEventListener('click', scrollPrev, false)
    nextButton.addEventListener('click', scrollNext, false)

    const buttonActiveHandle = this.toggleButtonsState(emblaApi, prevButton, nextButton)

    return () => {
      buttonActiveHandle()
      prevButton.removeEventListener('click', scrollPrev, false)
      nextButton.removeEventListener('click', scrollNext, false)
    }
  }

  dotsClickHandle(emblaApi, dots, onButtonClick) {
    let dotNodes = []

    const buttonClickHandle = () => {
      dots.innerHTML = emblaApi
        .scrollSnapList()
        .map(() => `<button class="${selectors.DOT_ITEM_CLASS}" type="button"></button>`)
        .join('')

      const scrollTo = (index) => {
        emblaApi.scrollTo(index)
        if (onButtonClick) onButtonClick(emblaApi)
      }

      dotNodes = Array.from(dots.querySelectorAll(`.${selectors.DOT_ITEM_CLASS}`))
      dotNodes.forEach((dotNode, index) => {
        dotNode.addEventListener('click', () => scrollTo(index), false)
      })
    }

    const dotsActiveHandle = () => {
      const previous = emblaApi.previousScrollSnap()
      const selected = emblaApi.selectedScrollSnap()

      removeClass(selectors.DOT_ITEM_SELECTED_CLASS, dotNodes[previous])
      addClass(selectors.DOT_ITEM_SELECTED_CLASS, dotNodes[selected])
    }

    emblaApi
      .on('init', buttonClickHandle)
      .on('reInit', buttonClickHandle)
      .on('init', dotsActiveHandle)
      .on('reInit', dotsActiveHandle)
      .on('select', dotsActiveHandle)

    return () => {
      dots.innerHTML = ''
    }
  }
}

export { EmblaCarouselInstance }
